import {
  ICall,
  IImmediateRealTimeAttendance,
  IScheduledRealTimeAttendance,
  IRealtimeNoShow,
} from '@/modules/RealtimeAttendance/realtime-attendance-types'
import {
  IFirebaseCall,
  IFirebaseDataSnapShot,
} from '@/modules/Firebase/firebase-types'
import {
  ATTENDANCE_STATES,
  callDataTranslate,
  queueDataTranslate,
} from '@/modules/RealtimeAttendance/realtime-attendance-constants'
import {
  IMMEDIATE_NODE,
  NOSHOW_NODE,
} from '@/modules/Firebase/firebase-constants'

export default class RealtimeAttendance {
  private _immediate: IImmediateRealTimeAttendance | null = null
  private _scheduled: IScheduledRealTimeAttendance[] = []
  private _noshow: IRealtimeNoShow | null = null
  private _call: ICall | null = null

  get immediate(): IImmediateRealTimeAttendance {
    return this._immediate
  }

  get scheduled(): IScheduledRealTimeAttendance[] {
    return this._scheduled
  }

  get noshow(): IRealtimeNoShow {
    return this._noshow
  }

  get isImmediate(): boolean {
    return !!this._immediate?.id
  }

  get nextScheduled(): IScheduledRealTimeAttendance {
    return this._scheduled.find(item => {
      return (
        item.status === ATTENDANCE_STATES.ATTENDANCE_STARTED ||
        item.status === ATTENDANCE_STATES.CALL_STARTED ||
        item.status === ATTENDANCE_STATES.CALL_RESTARTED ||
        item.status === ATTENDANCE_STATES.CALL_ENDED ||
        item.status === ATTENDANCE_STATES.ATTENDANCE_CANCELLED_MEDIC ||
        item.status === ATTENDANCE_STATES.ATTENDANCE_CANCELLED_TIME ||
        item.status === ATTENDANCE_STATES.ATTENDANCE_END ||
        item.status === ATTENDANCE_STATES.ATTENDANCE_CANCELLED_PATIENT_BLOCKED
      )
    })
  }

  get call(): ICall | null {
    return this._call
  }

  private fillImmediate() {
    if (!this._immediate.professionalId) {
      this._immediate.professionalName = 'consulta com'
      if (this._immediate.council === 'MEDICO')
        this._immediate.professionalSpeciality = 'Clínico Geral'
      else
        this._immediate.professionalSpeciality = this._immediate.councilString
    }
    this.setProgress()
  }

  private setProgress() {
    if (this._immediate.professionalId) this._immediate.progress = 100
    else this._immediate.progress = 100 / this._immediate.queuePosition
  }

  removeByFirebase(fibaseData: IFirebaseDataSnapShot, node: string): void {
    const { filaAtendimento, chamada } = fibaseData
    if (filaAtendimento) {
      if (node === IMMEDIATE_NODE) this._immediate = filaAtendimento
      else {
        this._scheduled = this._scheduled.filter(attendance => {
          return filaAtendimento.idProtocolo !== attendance.id
        })
      }
    }
    if (chamada && chamada.idChamada === this._call.callId) {
      this._call = null
    }
    if (node === NOSHOW_NODE) {
      this._noshow = null
    }
  }

  setImmediateByFirebase(firebaseData: IFirebaseDataSnapShot): void {
    const { filaAtendimento, chamada } = firebaseData
    let attendanceQueue: IImmediateRealTimeAttendance = {}
    if (filaAtendimento) {
      attendanceQueue = {
        id: filaAtendimento.idProtocolo,
        councilString: filaAtendimento.tipoProfissional,
        council: filaAtendimento.idTipoProfissional,
        npsAnswered: filaAtendimento.npsRespondido,
        patientOnline: filaAtendimento.pacienteOnline,
        status: filaAtendimento.status,
        queueSize: filaAtendimento.tamanhoFila,
        queuePosition: filaAtendimento.posicaoFila,
        estimatedTime: filaAtendimento.tempoEstimado,
        initialEstimatedTime: filaAtendimento.tempoEstimadoInicial,
        minimumWaitTime: filaAtendimento.tempoMinimoEspera,
        maximumWaitTime: filaAtendimento.tempoMaximoEspera,
        professionalId: filaAtendimento.idProfissional,
        professionalName: filaAtendimento.nomeProfissional,
        professionalPhoto: filaAtendimento.fotoProfissional,
        professionalSpeciality: filaAtendimento.especialidadeProfissional,
        professionalSex: filaAtendimento.sexoProfissional,
        attachments: filaAtendimento.anexos
          ? Object.values(filaAtendimento.anexos).map(attachment => ({
              id: attachment.idAnexo,
              description: attachment.legenda,
              url: attachment.urlDownload,
              attachmentType: null,
              date: new Date(),
              size: attachment.tamanho,
              extension: attachment.nomeExtensao,
              progress: 100,
              status: 'UPLOADED',
            }))
          : [],
        minimumHour: filaAtendimento.horaMinima,
        maximumHour: filaAtendimento.horaMaxima,
        attendanceId: filaAtendimento.idAtendimento,
      }
      this._immediate = attendanceQueue
      this.fillImmediate()
    }
    this.setCallByFirebase(chamada)
  }

  setScheduledByFirebase(firebaseData: IFirebaseDataSnapShot): void {
    const { filaAtendimento, chamada } = firebaseData
    const attendanceQueue: IScheduledRealTimeAttendance = {}
    if (filaAtendimento) {
      for (const item in filaAtendimento) {
        attendanceQueue[queueDataTranslate[item]] = filaAtendimento[item]
      }
      if (this._scheduled.find(att => att.id === attendanceQueue.id)) {
        this._scheduled = this._scheduled.map(att => {
          if (att.id === attendanceQueue.id) {
            return attendanceQueue
          }
          return att
        })
      } else {
        this._scheduled.push(attendanceQueue)
      }
    }
    this.setCallByFirebase(chamada)
  }

  private setCallByFirebase(firebaseCall: IFirebaseCall): void {
    const call = {}
    if (firebaseCall) {
      for (const item in firebaseCall) {
        call[callDataTranslate[item]] = firebaseCall[item]
      }
      this._call = call
    }
  }

  setNoShowByFirebase(firebaseData: IFirebaseDataSnapShot): void {
    const { noshow } = firebaseData

    if (noshow) {
      this._noshow = {
        patientId: noshow.idPaciente,
        date: noshow.dataNoShow,
        formattedDate: noshow.dataNoShowFormatada,
        noShowCount: noshow.quantidadeNoShow,
        screeningId: noshow.idTriagem,
        professionalType: noshow.tipoProfissional,
      }
    }
  }

  clear(): this {
    this._immediate = null
    this._scheduled = []
    this._call = null
    this._noshow = null
    return this
  }
}
